import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, ChevronRight, MessageCircleMore, Send, ShieldCheck } from "lucide-react";
import statusNone from "../assets/status/status-none.png";
import statusLight from "../assets/status/status-light.png";
import statusHeavy from "../assets/status/status-heavy.png";
import statusResisted from "../assets/status/status-resisted.png";
import hepapalMenuCard from "../assets/product/hepapal-menu-card.png";
import heppaWeek from "../assets/mascots/heppa-week.png";
import heppaRunningBlue from "../assets/mascots/heppa-running-blue.svg";

const PROMPT = "Heppa, help me log today.";
const RHYTHM_PROMPT = "Heppa, how much have I been drinking this week?";

type AnimationPhase =
  | "waiting"
  | "moving-to-input"
  | "focus-input"
  | "magnifying-input"
  | "restoring-input"
  | "typing"
  | "moving-to-send"
  | "click-send"
  | "sent"
  | "menu";

type LogAnimationPhase =
  | "idle"
  | "moving-to-light"
  | "pressing-light"
  | "logged"
  | "synced";

type CursorPoint = { left: number; top: number };

type RhythmAnimationPhase =
  | "waiting"
  | "moving-to-input"
  | "focus-input"
  | "typing"
  | "moving-to-send"
  | "click-send"
  | "question"
  | "reply"
  | "card";

const statusOptions = [
  { status: "none", label: "Didn't drink", icon: statusNone },
  { status: "light", label: "Had some drinks", icon: statusLight },
  { status: "heavy", label: "Drank a lot", icon: statusHeavy },
  { status: "resisted", label: "Resisted", icon: statusResisted },
];

const weekEntries = [
  { day: "M", date: 13, tone: "positive", label: "Resisted" },
  { day: "T", date: 14, tone: "calm", label: "Didn't drink" },
  { day: "W", date: 15, tone: "light", label: "Had some drinks" },
  { day: "T", date: 16, tone: "calm", label: "Didn't drink" },
  { day: "F", date: 17, tone: "heavy", label: "Drank a lot" },
  { day: "S", date: 18, tone: "calm", label: "Didn't drink", current: true },
  { day: "S", date: 19, tone: "empty", label: "Not logged" },
];

const weekStatusSummary = [
  { label: "Didn't drink", count: 3, tone: "calm" },
  { label: "Resisted", count: 1, tone: "positive" },
  { label: "Had some drinks", count: 1, tone: "light" },
  { label: "Drank a lot", count: 1, tone: "heavy" },
];

function FauxChatChrome({
  children,
  label,
  className = "",
}: {
  children: React.ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <div className={`faux-chat-window ${className}`} aria-label={label}>
      <div className="faux-chat-topbar">
        <div className="faux-chat-identity">
          <span className="faux-chat-mark" aria-hidden="true"><MessageCircleMore /></span>
          <div>
            <strong>Chat with Heppa</strong>
            <span>A gentle preview of the product flow</span>
          </div>
        </div>
        <span className="demo-badge">GENERIC AI</span>
      </div>
      {children}
    </div>
  );
}

function DemoCursor({
  phase,
  start,
  input,
  send,
  className = "",
}: {
  phase: AnimationPhase | RhythmAnimationPhase;
  start: CursorPoint;
  input: CursorPoint;
  send: CursorPoint;
  className?: string;
}) {
  const isClicking = phase === "focus-input" || phase === "click-send";
  const inputPhases = ["moving-to-input", "focus-input", "magnifying-input", "restoring-input", "typing"];
  const hiddenPhases = ["sent", "menu", "question", "reply", "card"];
  const point = phase === "waiting" ? start : inputPhases.includes(phase) ? input : send;
  const opacity = hiddenPhases.includes(phase) ? 0 : phase === "waiting" ? 0.72 : phase === "typing" ? 0.9 : 1;

  return (
    <motion.div
      className={`demo-cursor ${className}`.trim()}
      aria-hidden="true"
      animate={{ left: point.left, top: point.top, opacity }}
      transition={{
        duration: phase === "moving-to-input" || phase === "moving-to-send" ? 0.78 : 0.18,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <AnimatePresence>
        {isClicking ? (
          <motion.span
            className="cursor-click-ring"
            key={`${phase}-ring`}
            initial={{ opacity: 0.5, scale: 0.35 }}
            animate={{ opacity: 0, scale: 1.65 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.42 }}
          />
        ) : null}
      </AnimatePresence>
      <motion.svg
        viewBox="0 0 28 32"
        animate={{ scale: isClicking ? 0.88 : 1 }}
        transition={{ duration: 0.12 }}
      >
        <path
          d="M3 2.5v22.8l6.2-5.6 4.3 9.1 4.5-2.2-4.2-8.8h8.4L3 2.5Z"
          fill="#1f2933"
          stroke="#fff8ee"
          strokeLinejoin="round"
          strokeWidth="2.2"
        />
      </motion.svg>
    </motion.div>
  );
}

function LogDemoCursor({
  phase,
  start,
  target,
}: {
  phase: LogAnimationPhase;
  start: CursorPoint;
  target: CursorPoint;
}) {
  const isClicking = phase === "pressing-light";
  const isHidden = phase === "logged" || phase === "synced";
  const point = phase === "idle" ? start : target;

  return (
    <motion.div
      className="demo-cursor log-demo-cursor"
      aria-hidden="true"
      animate={{ left: point.left, top: point.top, opacity: isHidden ? 0 : phase === "idle" ? 0.72 : 1 }}
      transition={{
        duration: phase === "moving-to-light" ? 0.86 : isHidden ? 0.24 : 0.16,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <AnimatePresence>
        {isClicking ? (
          <motion.span
            className="cursor-click-ring"
            key="log-click-ring"
            initial={{ opacity: 0.55, scale: 0.35 }}
            animate={{ opacity: 0, scale: 1.65 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.44 }}
          />
        ) : null}
      </AnimatePresence>
      <motion.svg
        viewBox="0 0 28 32"
        animate={{ scale: isClicking ? 0.86 : 1 }}
        transition={{ duration: 0.12 }}
      >
        <path
          d="M3 2.5v22.8l6.2-5.6 4.3 9.1 4.5-2.2-4.2-8.8h8.4L3 2.5Z"
          fill="#1f2933"
          stroke="#fff8ee"
          strokeLinejoin="round"
          strokeWidth="2.2"
        />
      </motion.svg>
    </motion.div>
  );
}

export function SayItNaturallyDemo({ compact = false }: { compact?: boolean }) {
  const shouldReduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<AnimationPhase>(shouldReduceMotion ? "menu" : "waiting");
  const [typedText, setTypedText] = useState(shouldReduceMotion ? PROMPT : "");
  const [cycle, setCycle] = useState(0);
  const [cursorStart, setCursorStart] = useState<CursorPoint>({ left: 520, top: 132 });
  const [cursorInput, setCursorInput] = useState<CursorPoint>({ left: 220, top: 748 });
  const [cursorSend, setCursorSend] = useState<CursorPoint>({ left: 635, top: 748 });
  const stageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const sendRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (!stageRef.current || !inputRef.current || !sendRef.current) return;
      const stageRect = stageRef.current.getBoundingClientRect();
      const inputRect = inputRef.current.getBoundingClientRect();
      const sendRect = sendRef.current.getBoundingClientRect();
      setCursorStart({ left: stageRect.width * 0.77, top: stageRect.height * 0.18 });
      setCursorInput({
        left: inputRect.left - stageRect.left + inputRect.width * 0.3,
        top: inputRect.top - stageRect.top + inputRect.height * 0.5,
      });
      setCursorSend({
        left: sendRect.left - stageRect.left + sendRect.width * 0.5,
        top: sendRect.top - stageRect.top + sendRect.height * 0.5,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (stageRef.current) observer.observe(stageRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (shouldReduceMotion) {
      setPhase("menu");
      setTypedText(PROMPT);
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const wait = (milliseconds: number) => new Promise<void>((resolve) => {
      const timer = window.setTimeout(resolve, milliseconds);
      timers.push(timer);
    });

    const play = async () => {
      while (!cancelled) {
        setCycle((value) => value + 1);
        setTypedText("");
        setPhase("waiting");
        await wait(650);
        if (cancelled) return;

        setPhase("moving-to-input");
        await wait(820);
        setPhase("focus-input");
        await wait(340);
        setPhase("magnifying-input");
        await wait(2000);
        setPhase("restoring-input");
        await wait(520);
        setPhase("typing");

        for (let index = 1; index <= PROMPT.length; index += 1) {
          if (cancelled) return;
          setTypedText(PROMPT.slice(0, index));
          await wait(index === 7 || index === 22 ? 130 : 54);
        }

        await wait(520);
        setPhase("moving-to-send");
        await wait(760);
        setPhase("click-send");
        await wait(260);
        setPhase("sent");
        await wait(1100);
        setPhase("menu");
        await wait(3600);
      }
    };

    void play();

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [shouldReduceMotion]);

  const isSent = phase === "sent" || phase === "menu";
  const showMenu = phase === "menu";

  return (
    <FauxChatChrome
      className={compact ? "faux-chat-window-compact" : ""}
      label="Animated generic AI chat showing a natural HepaPal check-in"
    >
      <div className="animated-chat-stage" data-phase={phase} ref={stageRef}>
        <div className="chat-stage-copy">
          <AnimatePresence mode="wait">
            {isSent ? (
              <motion.div
                className="sent-conversation"
                key={`sent-${cycle}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.42 }}
              >
                <div className="chat-user-bubble">{PROMPT}</div>
                <motion.div
                  className="chat-assistant-line"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.42, delay: 0.36 }}
                >
                  <span className="assistant-dot" aria-hidden="true"><MessageCircleMore /></span>
                  <p>Of course. Tell Heppa how today went, in whatever words feel natural.</p>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                className="chat-ready-state"
                key={`ready-${cycle}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <span className="tiny-label">A QUIET START</span>
                <p>Write it the way you would say it.</p>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showMenu ? (
              <motion.figure
                className="demo-menu-slot"
                key={`menu-${cycle}`}
                initial={shouldReduceMotion ? false : { opacity: 0, y: 18, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -7, scale: 0.992 }}
                transition={{ duration: 0.58, ease: [0.22, 1, 0.36, 1] }}
              >
                <img
                  className="demo-menu-card"
                  src={hepapalMenuCard}
                  alt="HepaPal daily menu with four gentle logging choices and no log yet today"
                />
              </motion.figure>
            ) : null}
          </AnimatePresence>
        </div>

        <motion.div
          className={`simulated-input demo-one-input ${["focus-input", "magnifying-input", "restoring-input", "typing"].includes(phase) ? "is-focused" : ""} ${phase === "magnifying-input" ? "is-magnified" : ""}`}
          ref={inputRef}
          animate={phase === "magnifying-input" ? { scale: 1.07, y: -8 } : { scale: 1, y: 0 }}
          transition={{ duration: phase === "magnifying-input" ? 0.52 : 0.44, ease: [0.22, 1, 0.36, 1] }}
        >
          <input
            aria-label="Simulated message input"
            readOnly
            tabIndex={-1}
            value={isSent ? "" : typedText}
            placeholder="Message HepaPal..."
          />
          <span className={`simulated-send ${phase === "click-send" ? "is-clicked" : ""}`} aria-hidden="true" ref={sendRef}>
            <Send />
          </span>
        </motion.div>

        {!shouldReduceMotion ? (
          <DemoCursor phase={phase} start={cursorStart} input={cursorInput} send={cursorSend} />
        ) : null}
      </div>
    </FauxChatChrome>
  );
}

export function LogItDemo({
  initialMascot,
  selectedMascot,
}: {
  initialMascot: string;
  selectedMascot: string;
}) {
  const shouldReduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<LogAnimationPhase>(shouldReduceMotion ? "synced" : "idle");
  const [cursorStart, setCursorStart] = useState<CursorPoint>({ left: 760, top: 112 });
  const [cursorTarget, setCursorTarget] = useState<CursorPoint>({ left: 650, top: 325 });
  const stageRef = useRef<HTMLDivElement>(null);
  const lightOptionRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (!stageRef.current || !lightOptionRef.current) return;
      const stageRect = stageRef.current.getBoundingClientRect();
      const targetRect = lightOptionRef.current.getBoundingClientRect();
      setCursorStart({ left: stageRect.width * 0.82, top: stageRect.height * 0.17 });
      setCursorTarget({
        left: targetRect.left - stageRect.left + targetRect.width * 0.58,
        top: targetRect.top - stageRect.top + targetRect.height * 0.55,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (stageRef.current) observer.observe(stageRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (shouldReduceMotion) {
      setPhase("synced");
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const wait = (milliseconds: number) => new Promise<void>((resolve) => {
      const timer = window.setTimeout(resolve, milliseconds);
      timers.push(timer);
    });

    const play = async () => {
      while (!cancelled) {
        setPhase("idle");
        await wait(820);
        if (cancelled) return;
        setPhase("moving-to-light");
        await wait(900);
        setPhase("pressing-light");
        await wait(260);
        setPhase("logged");
        await wait(520);
        setPhase("synced");
        await wait(3400);
      }
    };

    void play();
    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [shouldReduceMotion]);

  const isSelected = phase === "pressing-light" || phase === "logged" || phase === "synced";
  const showLogged = phase === "logged" || phase === "synced";
  const daysTracked = phase === "synced" ? 25 : 24;

  return (
    <FauxChatChrome label="Animated generic AI chat showing HepaPal logging a day">
      <div className="static-chat-stage log-demo-stage" data-phase={phase} ref={stageRef}>
        <div className="chat-user-bubble">I had some drinks today.</div>
        <div className="chat-assistant-line">
          <span className="assistant-dot" aria-hidden="true"><MessageCircleMore /></span>
          <p>Thanks for checking in. Which one feels closest?</p>
        </div>

        <section className="hepapal-log-card" aria-label="HepaPal daily logging card preview">
          <div className="product-card-heading">
            <div>
              <span className="tiny-label">TODAY WITH HEPPA</span>
              <h3>Tap how today went</h3>
            </div>
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={isSelected ? "yellow-heppa" : "green-heppa"}
                src={isSelected ? selectedMascot : initialMascot}
                alt={isSelected ? "Yellow Heppa smiling" : "Green Heppa smiling"}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -3 }}
                transition={{ duration: 0.24 }}
              />
            </AnimatePresence>
          </div>
          <div className="status-grid" aria-label="Example daily status choices">
            {statusOptions.map((option) => {
              const selected = option.status === "light" && isSelected;
              return (
                <motion.span
                  className={`status-option ${selected ? "selected" : ""} ${option.status === "light" && phase === "pressing-light" ? "is-pressed" : ""}`}
                  key={option.status}
                  ref={option.status === "light" ? lightOptionRef : undefined}
                  animate={{ scale: option.status === "light" && phase === "pressing-light" ? 0.97 : 1 }}
                  transition={{ duration: 0.12 }}
                >
                  <img src={option.icon} alt="" />
                  <strong className="status-name">{option.label}</strong>
                </motion.span>
              );
            })}
          </div>

          <div className="log-confirmation-slot" aria-live="polite">
            <AnimatePresence initial={false}>
              {showLogged ? (
                <motion.p
                  className="log-confirmation"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -3 }}
                  transition={{ duration: 0.28 }}
                >
                  Logged <Check aria-hidden="true" />
                </motion.p>
              ) : null}
            </AnimatePresence>
          </div>

          <div className="log-stats" aria-label="Example tracking summary">
            <div><strong>7<small>d</small></strong><span>Alcohol-free</span></div>
            <div>
              <strong>
                <motion.b
                  key={daysTracked}
                  initial={{ opacity: 0.45, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.24 }}
                >
                  {daysTracked}
                </motion.b>
                <small>d</small>
              </strong>
              <span>Days tracked</span>
            </div>
            <div><strong>1<small>d</small></strong><span>Heavy days</span></div>
          </div>

          <div className="log-card-footer" aria-hidden="true">
            <span><img className="footer-heppa footer-heppa-week" src={heppaWeek} alt="" /><b>This Week</b></span>
            <span><img className="footer-heppa footer-heppa-running" src={heppaRunningBlue} alt="" /><b>My HepaPal</b></span>
          </div>
        </section>

        <div className="simulated-input static-input">
          <input aria-label="Static simulated message" readOnly tabIndex={-1} value="" placeholder="Message HepaPal..." />
          <span className="simulated-send" aria-hidden="true"><Send /></span>
        </div>

        {!shouldReduceMotion ? (
          <LogDemoCursor phase={phase} start={cursorStart} target={cursorTarget} />
        ) : null}
      </div>
    </FauxChatChrome>
  );
}

export function RhythmDemo({ mascot }: { mascot: string }) {
  const shouldReduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<RhythmAnimationPhase>(shouldReduceMotion ? "card" : "waiting");
  const [typedText, setTypedText] = useState(shouldReduceMotion ? RHYTHM_PROMPT : "");
  const [cycle, setCycle] = useState(0);
  const [cursorStart, setCursorStart] = useState<CursorPoint>({ left: 520, top: 132 });
  const [cursorInput, setCursorInput] = useState<CursorPoint>({ left: 220, top: 748 });
  const [cursorSend, setCursorSend] = useState<CursorPoint>({ left: 635, top: 748 });
  const stageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const sendRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (!stageRef.current || !inputRef.current || !sendRef.current) return;
      const stageRect = stageRef.current.getBoundingClientRect();
      const inputRect = inputRef.current.getBoundingClientRect();
      const sendRect = sendRef.current.getBoundingClientRect();
      setCursorStart({ left: stageRect.width * 0.77, top: stageRect.height * 0.18 });
      setCursorInput({
        left: inputRect.left - stageRect.left + inputRect.width * 0.3,
        top: inputRect.top - stageRect.top + inputRect.height * 0.5,
      });
      setCursorSend({
        left: sendRect.left - stageRect.left + sendRect.width * 0.5,
        top: sendRect.top - stageRect.top + sendRect.height * 0.5,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (stageRef.current) observer.observe(stageRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (shouldReduceMotion) {
      setPhase("card");
      setTypedText(RHYTHM_PROMPT);
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const wait = (milliseconds: number) => new Promise<void>((resolve) => {
      const timer = window.setTimeout(resolve, milliseconds);
      timers.push(timer);
    });

    const play = async () => {
      while (!cancelled) {
        setCycle((value) => value + 1);
        setTypedText("");
        setPhase("waiting");
        await wait(520);
        if (cancelled) return;
        setPhase("moving-to-input");
        await wait(820);
        setPhase("focus-input");
        await wait(300);
        setPhase("typing");
        for (let index = 1; index <= RHYTHM_PROMPT.length; index += 1) {
          if (cancelled) return;
          setTypedText(RHYTHM_PROMPT.slice(0, index));
          await wait(index === 7 || index === 29 ? 120 : 46);
        }
        await wait(420);
        setPhase("moving-to-send");
        await wait(760);
        setPhase("click-send");
        await wait(260);
        setPhase("question");
        await wait(760);
        setPhase("reply");
        await wait(880);
        setPhase("card");
        await wait(4200);
      }
    };

    void play();
    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [shouldReduceMotion]);

  const showQuestion = phase === "question" || phase === "reply" || phase === "card";
  const showReply = phase === "reply" || phase === "card";
  const showCard = phase === "card";

  return (
    <FauxChatChrome label="Animated generic AI chat showing a HepaPal weekly rhythm">
      <div className="static-chat-stage rhythm-stage" data-phase={phase} ref={stageRef}>
        <div className="rhythm-conversation" aria-live="polite">
          <AnimatePresence>
            {showQuestion ? (
              <motion.div
                className="chat-user-bubble"
                key={`week-question-${cycle}`}
                initial={{ opacity: 0, y: 8, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
              >
                {RHYTHM_PROMPT}
              </motion.div>
            ) : null}
          </AnimatePresence>

          <AnimatePresence>
            {showReply ? (
              <motion.div
                className="chat-assistant-line"
                key={`week-reply-${cycle}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="assistant-dot" aria-hidden="true"><MessageCircleMore /></span>
                <p>Let me pull up your week with Heppa.</p>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        <div className="week-card-slot">
          <AnimatePresence>
            {showCard ? (
              <motion.section
                className="week-rhythm-card"
                key={`week-card-${cycle}`}
                aria-label="Example weekly HepaPal rhythm through Saturday the eighteenth"
                initial={{ opacity: 0, y: 18, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -7, scale: 0.992 }}
                transition={{ duration: 0.58, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.div
                  className="week-callout"
                  initial={{ opacity: 0, y: 7 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.12 }}
                >
                  <img src={mascot} alt="Blue Heppa smiling" />
                  <p>You paused 4 days this week. Heppa keeps every one gently in view.</p>
                </motion.div>

                <motion.div
                  className="week-calendar"
                  role="img"
                  aria-label="Week of the thirteenth through nineteenth, with six days logged through Saturday the eighteenth"
                  initial={{ opacity: 0, y: 7 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  {weekEntries.map((entry) => (
                    <span className={`week-day ${entry.current ? "is-current" : ""}`} key={entry.date}>
                      <strong>{entry.day}</strong>
                      <i className={`week-dot ${entry.tone}`} aria-hidden="true" />
                      <small>{entry.date}</small>
                      <span className="sr-only">{entry.label}</span>
                    </span>
                  ))}
                </motion.div>

                <motion.div
                  className="week-status-list"
                  aria-label="Weekly status counts"
                  initial={{ opacity: 0, y: 7 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.28 }}
                >
                  {weekStatusSummary.map((item, index) => (
                    <motion.div
                      className="week-status-row"
                      key={item.label}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.32, delay: 0.34 + index * 0.06 }}
                    >
                      <i className={`week-dot ${item.tone}`} aria-hidden="true" />
                      <span className="status-name">{item.label}</span>
                      <strong>{item.count}d</strong>
                      <ChevronRight aria-hidden="true" />
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div
                  className="week-metrics"
                  initial={{ opacity: 0, y: 7 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.58 }}
                >
                  <div><strong>4</strong><span>Alcohol-free</span></div>
                  <div><strong>27</strong><span>Days tracked</span></div>
                </motion.div>
              </motion.section>
            ) : null}
          </AnimatePresence>
        </div>

        <div
          className={`simulated-input static-input ${phase === "focus-input" || phase === "typing" ? "is-focused" : ""}`}
          ref={inputRef}
        >
          <input
            aria-label="Simulated weekly question input"
            readOnly
            tabIndex={-1}
            value={showQuestion ? "" : typedText}
            placeholder="Message HepaPal..."
          />
          <span className={`simulated-send ${phase === "click-send" ? "is-clicked" : ""}`} aria-hidden="true" ref={sendRef}><Send /></span>
        </div>

        {!shouldReduceMotion ? (
          <DemoCursor
            className="rhythm-demo-cursor"
            phase={phase}
            start={cursorStart}
            input={cursorInput}
            send={cursorSend}
          />
        ) : null}
      </div>
    </FauxChatChrome>
  );
}

export function DemoDisclaimer() {
  return (
    <p className="demo-disclaimer">
      <ShieldCheck aria-hidden="true" />
      Product-flow illustration. No official chat interface or endorsement implied.
    </p>
  );
}
